/**
 * Batch 3 Part B - Admin user detail full page.
 *
 * Route: /admin/users/:userId (admin+ gated)
 *
 * Sections:
 *   - Identity: name, email, phone, role, sign-up date
 *   - Profile: thumbnail strip (no "photo" or "image" words in alt), age/
 *     gender/location, socials, bio, interests, activities
 *   - Progression: level, rank, points total, achievements count
 *   - Recent registrations: last N with event title + status + vetted flag
 *   - Recent point events: last 10 ledger rows
 *   - Referrals: code + downstream attributions count
 *
 * All data via GET /api/admin/users/{userId}/detail (existing endpoint,
 * enriched with event_title / event_date / event_slug in Batch 3).
 *
 * CRITICAL a11y: image alt text uses the user's name only, never the
 * words "photo" or "image" (jsx-a11y/img-redundant-alt would block the
 * prod build). No eslint-disable directives anywhere.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  ArrowLeft, Mail, Phone as PhoneIcon, MapPin, Calendar as CalendarIcon,
  TrendingUp, Trophy, Ticket, Share2, ExternalLink, ShieldCheck, BadgeCheck,
  ChevronDown, ChevronRight, ImageIcon, Award, Clock, FileJson, Users, Eye,
  Loader2, RefreshCw, MoreVertical, UploadCloud,
} from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import CopyJsonButton from './CopyJsonButton';
import DataQualityCard from './components/DataQualityCard';
import UserTimelineCard from './components/UserTimelineCard';
import { VerdictChip } from '@/components/VerdictChip';

/**
 * Bundle 163 - derive the same verdict on the FE for the
 * SIGNUP INCOMPLETE chip. Mirrors the shared backend helper at
 * routes/verdict_helper.py::compute_verdict. Kept as a local
 * function (not a shared util) because it consumes the same
 * user shape returned by /admin/users/{id}/detail and we want
 * source-inspection to prove the logic parity.
 */
const BUNDLE_149_CUTOFF_ISO = '2026-08-31';
const computeSignupVerdict = (u) => {
  if (!u) return null;
  if (u.signup_completion_required) return 'FLAGGED';
  const created = (u.created_at || '').slice(0, 10);
  const postCutoff = created >= BUNDLE_149_CUTOFF_ISO;
  const hasDob = !!(u.profile && u.profile.dob);
  if (!postCutoff) return 'LEGACY';
  if (hasDob) return null; // COMPLETE - no chip needed.
  return 'LEAK';
};

const STATUS_STYLE = {
  confirmed: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  pending_payment: 'bg-amber-50 text-amber-800 border-amber-200',
  waitlisted: 'bg-blue-50 text-blue-800 border-blue-200',
  submitted: 'bg-neutral-50 text-neutral-700 border-neutral-200',
  cancelled: 'bg-red-50 text-red-800 border-red-200',
  checked_in: 'bg-secondary-50 text-secondary-800 border-secondary-200',
  no_show: 'bg-neutral-100 text-neutral-500 border-neutral-200',
  draft: 'bg-neutral-50 text-neutral-500 border-neutral-200',
};

const ROLE_STYLE = {
  super_admin: 'bg-accent-50 text-accent-800 border-accent-300',
  admin: 'bg-primary-50 text-primary-800 border-primary-200',
  ambassador: 'bg-secondary-50 text-secondary-800 border-secondary-200',
  user: 'bg-neutral-50 text-neutral-700 border-neutral-200',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (_) { return ''; }
};

const Kv = ({ label, value }) => value ? (
  <div className="text-sm">
    <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
    <div className="text-primary-900">{value}</div>
  </div>
) : null;

const StatCard = ({ label, value, icon: Icon, testid }) => (
  <div
    className="rounded-lg border border-neutral-200 p-3 flex items-start gap-3"
    data-testid={testid}
  >
    {Icon && <Icon className="h-4 w-4 text-secondary-700 shrink-0 mt-0.5" />}
    <div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-display text-xl font-bold text-primary-900 leading-tight">
        {value}
      </div>
    </div>
  </div>
);

const AdminUserDetailPage = () => {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isAdmin = currentUser?.role === 'admin' || isSuperAdmin;
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [optOut, setOptOut] = useState(false);
  const [emqOptOut, setEmqOptOut] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const [impersonateReason, setImpersonateReason] = useState('');
  const [reloadTick, setReloadTick] = useState(0);
  const bumpReload = () => setReloadTick((n) => n + 1);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const { data } = await api.get(`/admin/users/${userId}/detail`);
        if (mounted) {
          setDetail(data);
          setOptOut(!!(data?.user?.weekly_digest_opt_out));
          setEmqOptOut(!!(data?.user?.emq_alerts_opt_out));
        }
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || 'Failed to load user detail.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [userId, reloadTick]);

  if (loading) {
    return (
      <div className="p-8 text-sm text-neutral-500" data-testid="admin-user-detail-loading">
        Loading user...
      </div>
    );
  }
  if (error || !detail) {
    return (
      <div className="p-8" data-testid="admin-user-detail-error">
        <Link to="/admin/users" className="inline-flex items-center gap-1 text-secondary-700 hover:text-secondary-900 text-sm mb-4">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to users
        </Link>
        <div className="text-sm text-red-700">{error || 'User not found.'}</div>
      </div>
    );
  }

  const u = detail.user || {};
  // Bundle 167 A2 · read top-level `user.dob` as fallback for the
  // legacy /api/auth/register path (which stamped DOB via
  // build_confirmation_patch at top level, not inside profile).
  // Bundle 167 A3 · derive age from any DOB source when profile.age
  // is empty. Both are read-side patches; the B migration back-
  // populates profile.dob + profile.age going forward.
  const rawProfile = u.profile || {};
  const effectiveDob = rawProfile.dob || u.dob || '';
  const deriveAgeFromDob = (iso) => {
    if (!iso || typeof iso !== 'string') return null;
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const dob = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (Number.isNaN(dob.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const beforeBday = now.getMonth() < dob.getMonth()
      || (now.getMonth() === dob.getMonth()
          && now.getDate() < dob.getDate());
    if (beforeBday) age -= 1;
    return age >= 0 && age < 130 ? age : null;
  };
  const effectiveAge = rawProfile.age != null
    ? rawProfile.age
    : deriveAgeFromDob(effectiveDob);
  const p = {
    ...rawProfile,
    dob: effectiveDob || rawProfile.dob,
    age: effectiveAge != null ? effectiveAge : rawProfile.age,
  };
  const location = p.location || {};
  const photos = p.photos || [];
  const displayName = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
  const prog = detail.progression || null;
  const regs = detail.registrations || [];
  const ledger = detail.recent_ledger || [];
  const ref = detail.referral_code || null;
  const refAttribCount = (detail.referral_attributions || []).length;

  return (
    <div className="p-6 max-w-5xl space-y-6" data-testid="admin-user-detail-page">
      {/* Back link + admin actions toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-1 text-secondary-700 hover:text-secondary-900 text-sm"
          data-testid="admin-user-detail-back-link"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to users
        </Link>
        {isAdmin && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => { setImpersonateReason(''); setImpersonateOpen(true); }}
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
            data-testid="admin-user-detail-view-as-user-btn"
          >
            <Eye className="h-4 w-4 mr-1.5" />
            View as user (preview)
          </Button>
        )}
      </div>

      {/* Impersonation reason dialog */}
      <Dialog open={impersonateOpen} onOpenChange={setImpersonateOpen}>
        <DialogContent data-testid="admin-user-detail-impersonate-dialog">
          <DialogHeader>
            <DialogTitle>View as {displayName}</DialogTitle>
            <DialogDescription>
              Opens a read-only preview of this user&apos;s profile in a new tab.
              Nothing you do there will be saved. Add an optional reason for
              the audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-neutral-500">
              Reason (optional)
            </label>
            <Input
              value={impersonateReason}
              onChange={(e) => setImpersonateReason(e.target.value.slice(0, 500))}
              placeholder="e.g. support ticket #1234, verifying reported bug"
              data-testid="admin-user-detail-impersonate-reason-input"
              maxLength={500}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setImpersonateOpen(false)}
              data-testid="admin-user-detail-impersonate-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                const params = new URLSearchParams({ admin_preview: u.id });
                const trimmed = (impersonateReason || '').trim();
                if (trimmed) params.set('reason', trimmed);
                const url = `/profile?${params.toString()}`;
                window.open(url, '_blank', 'noopener,noreferrer');
                setImpersonateOpen(false);
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="admin-user-detail-impersonate-open"
            >
              <ExternalLink className="h-4 w-4 mr-1.5" />
              Open read-only preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bundle 44 - per-user Data Quality card at the top of the page. */}
      {isAdmin ? (
        <DataQualityCard userId={u.id} onAfterRepair={bumpReload} />
      ) : null}

      {/* Identity header */}
      <div className="flex items-start gap-4 flex-wrap">
        {photos[0] && (
          <img
            src={photos[0].thumb_url || photos[0].url}
            alt={`${displayName} profile`}
            className="w-24 h-24 rounded-xl object-cover bg-neutral-100 shrink-0"
            data-testid="admin-user-detail-avatar"
            loading="lazy"
          />
        )}
        <div className="flex-1 min-w-[240px]">
          <h1 className="font-display text-3xl font-bold text-primary-900" data-testid="admin-user-detail-name">
            {displayName}
          </h1>
          <div className="mt-1 flex items-center gap-3 flex-wrap text-sm text-neutral-600">
            <span className="inline-flex items-center gap-1">
              <Mail className="h-3 w-3" /> {u.email}
            </span>
            {u.phone && (
              <span className="inline-flex items-center gap-1">
                <PhoneIcon className="h-3 w-3" /> {u.phone}
              </span>
            )}
            <Badge
              variant="outline"
              className={ROLE_STYLE[u.role] || 'bg-neutral-50 text-neutral-700'}
              data-testid="admin-user-detail-role"
            >
              {(u.role || 'user').replace('_', ' ')}
            </Badge>
            {computeSignupVerdict(u) ? (
              <span data-testid="admin-user-detail-signup-verdict">
                <VerdictChip
                  verdict={computeSignupVerdict(u)}
                  prefix="SIGNUP INCOMPLETE"
                  testId="admin-user-detail-signup-verdict-chip"
                />
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
              <CalendarIcon className="h-3 w-3" /> Joined {fmtDate(u.created_at)}
            </span>
          </div>
          {computeSignupVerdict(u) && u.signup_completion_reason ? (
            <div
              className="mt-1 text-[11px] text-neutral-500 font-mono"
              data-testid="admin-user-detail-signup-reason"
            >
              Reason: {u.signup_completion_reason}
            </div>
          ) : null}
          {(location.city || location.state || location.country) && (
            <div className="mt-1 text-xs text-neutral-500 inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {[location.city, location.state, location.country].filter(Boolean).join(', ')}
            </div>
          )}
          {(u.role === 'admin' || u.role === 'super_admin') && (
            <label
              className="mt-3 inline-flex items-center gap-2 text-xs text-neutral-700 cursor-pointer select-none"
              data-testid="admin-user-detail-digest-opt-out-label"
            >
              <input
                type="checkbox"
                checked={!!optOut}
                onChange={async (e) => {
                  const next = e.target.checked;
                  setOptOut(next);
                  try {
                    await api.post(`/admin/users/${u.id}/weekly-digest`, { opt_out: next });
                  } catch (_) {
                    setOptOut(!next);
                  }
                }}
                data-testid="admin-user-detail-digest-opt-out"
                className="accent-primary-800"
              />
              Opt out of the weekly pixel analytics digest email
            </label>
          )}
          {(u.role === 'admin' || u.role === 'super_admin') && (
            <label
              className="mt-2 inline-flex items-center gap-2 text-xs text-neutral-700 cursor-pointer select-none"
              data-testid="admin-user-detail-emq-opt-out-label"
            >
              <input
                type="checkbox"
                checked={!!emqOptOut}
                onChange={async (e) => {
                  const next = e.target.checked;
                  setEmqOptOut(next);
                  try {
                    await api.post(`/admin/users/${u.id}/emq-alerts`, { opt_out: next });
                  } catch (_) {
                    setEmqOptOut(!next);
                  }
                }}
                data-testid="admin-user-detail-emq-opt-out"
                className="accent-primary-800"
              />
              Opt out of Meta EMQ drop alerts
            </label>
          )}
        </div>
      </div>

      {/* Progression stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label={detail.progression_shaped?.rank_display ? `Level (${detail.progression_shaped.rank_display})` : "Level"}
          value={prog ? `L${prog.level}` : '-'}
          icon={TrendingUp}
          testid="admin-user-detail-stat-level"
        />
        <StatCard
          label="Points"
          value={prog ? (prog.points_total || 0).toLocaleString() : '0'}
          testid="admin-user-detail-stat-points"
        />
        <StatCard
          label="Achievements"
          value={detail.achievement_count_public || 0}
          icon={Trophy}
          testid="admin-user-detail-stat-achievements"
        />
        <StatCard
          label="RSVPs"
          value={regs.length}
          icon={Ticket}
          testid="admin-user-detail-stat-rsvps"
        />
      </div>

      {/* Gamification hero strip (parity with user-facing ProgressionHero) */}
      <ProgressionHeroAdmin
        shaped={detail.progression_shaped}
        recent={(detail.recent_ledger || []).slice(0, 5)}
      />

      {/* Profile photo strip (extra thumbs beyond avatar) */}
      {photos.length > 1 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Profile pictures</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-2" data-testid="admin-user-detail-photo-strip">
              {photos.map((ph, i) => (
                <img
                  key={ph.id || i}
                  src={ph.thumb_url || ph.url}
                  alt={`${displayName} profile ${i + 1}`}
                  className="aspect-square rounded-lg object-cover bg-neutral-100"
                  data-testid={`admin-user-detail-thumb-${i}`}
                  loading="lazy"
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bundle 127 - fine-grained RBAC (super_admin only) */}
      {isSuperAdmin && (u.role === 'admin' || u.role === 'super_admin') && (
        <RbacPermissionsCard userId={u.id} role={u.role} />
      )}

      {/* Grid: registrations + ledger */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-sm">Recent registrations</CardTitle>
            <span className="text-xs text-neutral-400">{regs.length}</span>
          </CardHeader>
          <CardContent>
            {regs.length === 0 ? (
              <div className="text-sm text-neutral-500 italic">No registrations yet.</div>
            ) : (
              <ul className="space-y-2" data-testid="admin-user-detail-registrations">
                {regs.slice(0, 10).map((r) => (
                  <li
                    key={r.id}
                    className="flex items-start justify-between gap-2 p-2 rounded border border-neutral-100 hover:bg-neutral-50"
                    data-testid={`admin-user-detail-reg-${r.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-primary-900 truncate flex items-center gap-2">
                        {r.event_slug ? (
                          <Link
                            to={`/admin/events/${r.event_id}/registrations`}
                            className="hover:text-secondary-700 hover:underline truncate"
                          >
                            {r.event_title || r.event_id}
                          </Link>
                        ) : (
                          <span className="truncate">{r.event_title || r.event_id}</span>
                        )}
                        {r.admin_vetted && (
                          <Badge
                            variant="outline"
                            className="bg-emerald-50 text-emerald-800 border-emerald-200 h-5 text-[10px] px-1.5 gap-0.5 shrink-0"
                            data-testid={`admin-user-detail-reg-vetted-${r.id}`}
                          >
                            <BadgeCheck className="h-3 w-3" /> Vetted
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-neutral-500 flex items-center gap-2 mt-0.5">
                        <span>{fmtDate(r.created_at)}</span>
                        {r.ticket_code && (
                          <span className="font-mono text-secondary-700">{r.ticket_code}</span>
                        )}
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className={`${STATUS_STYLE[r.status] || 'bg-neutral-50 text-neutral-700'} h-5 text-[10px] px-1.5 shrink-0`}
                    >
                      {r.status}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-sm">Recent point events</CardTitle>
            <span className="text-xs text-neutral-400">{ledger.length}</span>
          </CardHeader>
          <CardContent>
            {ledger.length === 0 ? (
              <div className="text-sm text-neutral-500 italic">No point events yet.</div>
            ) : (
              <ul className="space-y-1.5" data-testid="admin-user-detail-ledger">
                {ledger.map((row) => (
                  <li
                    key={row.id}
                    className="flex items-center justify-between gap-2 text-sm p-2 rounded border border-neutral-100"
                    data-testid={`admin-user-detail-ledger-${row.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-primary-900 font-medium truncate">{row.kind}</div>
                      <div className="text-[11px] text-neutral-500 truncate">
                        {row.reason || row.idempotency_key || fmtDate(row.created_at)}
                      </div>
                    </div>
                    <div className={`font-mono text-sm shrink-0 ${row.points >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {row.points >= 0 ? '+' : ''}{row.points}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Referrals row */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm inline-flex items-center gap-2"><Share2 className="h-3.5 w-3.5" /> Referrals</CardTitle></CardHeader>
        <CardContent>
          {ref ? (
            <div className="flex items-center gap-4 flex-wrap" data-testid="admin-user-detail-referrals">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Code</div>
                <div className="font-mono font-semibold text-primary-900" data-testid="admin-user-detail-referral-code">
                  {ref.code}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Downstream</div>
                <div className="font-display text-xl font-bold text-primary-900" data-testid="admin-user-detail-referral-downstream">
                  {refAttribCount}
                </div>
              </div>
              <Link
                to={`/admin/referrals?code=${encodeURIComponent(ref.code)}`}
                className="ml-auto inline-flex items-center gap-1 text-secondary-700 hover:text-secondary-900 text-sm"
                data-testid="admin-user-detail-referrals-open"
              >
                Open in Referrals CMS <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          ) : (
            <div className="text-sm text-neutral-500 italic">This user has not been issued a referral code.</div>
          )}
        </CardContent>
      </Card>

      {/* Profile grid */}
      <ProfileSection p={p} location={location} />

      {/* Bundle 70 Task 4 - unified activity timeline across pixels,
          emails, RSVPs, payments, nudges, sessions, media, and points. */}
      <UserTimelineCard userId={u.id} />

      {/* All profile sections (letter A-G) - shows every field the
          user filled anywhere. Turns silent-missing-field into
          visible-present-or-not. */}
      <ProfileSectionsGrid sections={detail.profile_sections || []} />

      {/* Photos gallery - every photo the user uploaded. */}
      <PhotosGallery
        photos={detail.photos || []}
        displayName={displayName}
        targetEmail={u.email}
        userId={u.id}
        onChanged={bumpReload}
      />

      {/* Bundle 34 Task 3 - Edit on behalf. Available to admin+.
          Silent (user is NOT notified). Every save is hashed +
          persisted to admin_audit_entries. */}
      {isAdmin && <EditOnBehalfCard user={u} onSaved={bumpReload} />}

      {/* Bundle 36 Task A - Recent admin actions on this user. */}
      {isAdmin && <AdminActionsPreviewCard userId={u.id} />}

      {/* Achievements - hydrated with title/rarity/bonus_points. */}
      <AchievementsBlock unlocks={detail.achievements || []} />

      {/* Point events - full ledger (up to 200). */}
      <PointEventsBlock events={detail.point_events || []} />

      {/* Referrals downstream - who they've referred. */}
      <ReferralDownstreamBlock
        code={detail.referral_code}
        downstream={detail.referral_downstream || []}
      />

      {/* Raw user document - debug last-resort so admin never wonders
          "why is Age = 35 the only visible field?". Shows the full
          sanitized document (secrets stripped server-side). */}
      <RawUserBlock user={detail.raw_user || u} />

      {/* Bundle 29a - hydration diagnostic (what would the weak-value
          hydrator do for this user without executing). Super admin only. */}
      {isSuperAdmin && <HydrationDebugBlock userId={u.id} />}
    </div>
  );
};

/**
 * Iteration 22 hotfix - Profile section with defensive "empty profile"
 * UX. When fewer than 3 core fields are populated, an inline hint links
 * out to Maintenance so admins can run the backfill instead of guessing
 * why AdminUserDetailPage looks blank. Turns the silent-fail into a
 * visible signal.
 */
const ProfileSection = ({ p, location }) => {
  const socials = p.social_handles || {};
  const hasSocials = !!(socials.instagram || socials.linkedin
                        || socials.tiktok || socials.facebook
                        || socials.twitter || socials.x);
  const populatedCount = [
    p.age, p.gender, p.dob, p.occupation, p.about_me,
    (Array.isArray(p.languages) && p.languages.length) || null,
    (Array.isArray(p.interests) && p.interests.length) || null,
    location.city || location.state || location.country,
    hasSocials || null,
  ].filter((v) => v !== null && v !== undefined && v !== '' && v !== false).length;
  const isSparse = populatedCount < 3;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          Profile
          {isSparse && (
            <span
              className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 text-amber-800 text-[10px] font-semibold px-2 py-0.5"
              data-testid="admin-user-detail-profile-sparse-chip"
            >
              {populatedCount}/9 fields populated
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isSparse && (
          <div
            className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 leading-relaxed"
            data-testid="admin-user-detail-profile-empty-hint"
          >
            <div className="font-semibold mb-1">This profile looks sparse.</div>
            <div>
              If this user has RSVPs on file, their answers can back-fill the
              profile. Run <span className="font-mono">backfill-profiles-from-rsvp</span> at
              {' '}
              <Link
                to="/admin/maintenance"
                className="underline font-semibold text-amber-900 hover:text-amber-950"
                data-testid="admin-user-detail-profile-open-maintenance"
              >
                Maintenance
              </Link>
              . Preview first, then Apply.
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kv label="Age" value={p.age} />
          <Kv label="Gender" value={p.gender} />
          <Kv label="DOB" value={p.dob} />
          <Kv label="City" value={location.city} />
          <Kv label="State" value={location.state} />
          <Kv label="Country" value={location.country} />
          <Kv label="Occupation" value={p.occupation} />
          <Kv label="Languages" value={Array.isArray(p.languages) ? p.languages.join(', ') : p.languages} />
        </div>
        {hasSocials && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Social profiles</div>
            <div className="flex flex-wrap gap-3 text-sm" data-testid="admin-user-detail-page-socials">
              {socials.instagram && (
                <a href={`https://instagram.com/${socials.instagram.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="text-primary-700 hover:underline">
                  Instagram: @{socials.instagram.replace('@','')}
                </a>
              )}
              {socials.linkedin && (
                <a href={socials.linkedin.startsWith('http') ? socials.linkedin : `https://linkedin.com/in/${socials.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-primary-700 hover:underline">
                  LinkedIn: {socials.linkedin}
                </a>
              )}
              {socials.tiktok && (
                <a href={`https://tiktok.com/@${socials.tiktok.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="text-primary-700 hover:underline">
                  TikTok: @{socials.tiktok.replace('@','')}
                </a>
              )}
              {socials.facebook && (
                <a href={socials.facebook.startsWith('http') ? socials.facebook : `https://facebook.com/${socials.facebook}`} target="_blank" rel="noopener noreferrer" className="text-primary-700 hover:underline">
                  Facebook: {socials.facebook}
                </a>
              )}
              {socials.twitter && (
                <a href={socials.twitter.startsWith('http') ? socials.twitter : `https://twitter.com/${socials.twitter.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="text-primary-700 hover:underline" data-testid="admin-user-detail-social-twitter">
                  Twitter: @{socials.twitter.replace('@','')}
                </a>
              )}
              {socials.x && (
                <a href={socials.x.startsWith('http') ? socials.x : `https://x.com/${socials.x.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="text-primary-700 hover:underline" data-testid="admin-user-detail-social-x">
                  X: @{socials.x.replace('@','')}
                </a>
              )}
            </div>
          </div>
        )}
        {p.about_me && (
          <div className="text-sm text-primary-900/90 whitespace-pre-wrap p-3 rounded-lg bg-neutral-50">
            {p.about_me}
          </div>
        )}
        {Array.isArray(p.interests) && p.interests.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Interests</div>
            <div className="flex flex-wrap gap-1.5" data-testid="admin-user-detail-interests">
              {p.interests.map((it) => (
                <Badge key={it} variant="outline" className="bg-secondary-50 text-secondary-800 border-secondary-200 h-6 text-xs">
                  {it}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Bundle 29a - HydrationDebugBlock renders an on-demand diagnostic of
// what the weak-value hydrator would do for the target user. Read-only.
const HydrationDebugBlock = ({ userId }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const run = async () => {
    setLoading(true); setError('');
    try {
      const { data: res } = await api.get(`/admin/users/${userId}/hydration-debug`);
      setData(res);
      setOpen(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Diagnostic failed');
    } finally { setLoading(false); }
  };
  const rows = data ? Object.entries(data.would_update || {}) : [];
  return (
    <Card data-testid="admin-user-detail-hydration-debug">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between gap-2">
          <span>Hydration diagnostic</span>
          <Button
            type="button" size="sm" variant="outline"
            onClick={run} disabled={loading}
            data-testid="admin-user-detail-hydration-debug-run"
          >
            {loading ? 'Running...' : 'Run hydration diagnostic'}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {error && (
          <div className="rounded-md border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2 text-xs">{error}</div>
        )}
        {data && (
          <>
            <div className="text-xs text-neutral-600">
              {rows.length} field(s) would be updated on next hydrate run.
            </div>
            {rows.length > 0 && (
              <div className="rounded-md border border-neutral-200 overflow-hidden text-xs" data-testid="admin-user-detail-hydration-would-update">
                <table className="w-full">
                  <thead className="bg-neutral-50 text-neutral-600 text-[10px] uppercase tracking-widest">
                    <tr>
                      <th className="text-left px-2 py-1.5">Field</th>
                      <th className="text-left px-2 py-1.5">From</th>
                      <th className="text-left px-2 py-1.5">To</th>
                      <th className="text-left px-2 py-1.5">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(([k, v]) => (
                      <tr key={k} className="border-t border-neutral-100">
                        <td className="px-2 py-1.5 font-mono">{k}</td>
                        <td className="px-2 py-1.5 text-neutral-500">{String(v.from ?? '(empty)')}</td>
                        <td className="px-2 py-1.5 font-semibold text-primary-900">{String(v.to)}</td>
                        <td className="px-2 py-1.5 text-neutral-600">{v.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button
              type="button"
              onClick={() => setOpen((s) => !s)}
              className="text-[11px] text-secondary-700 hover:underline"
              data-testid="admin-user-detail-hydration-debug-toggle-raw"
            >
              {open ? 'Hide' : 'Show'} raw JSON
            </button>
            {open && (
              <pre
                className="text-[10px] bg-neutral-50 border border-neutral-200 rounded p-2 max-h-72 overflow-auto"
                data-testid="admin-user-detail-hydration-debug-json"
              >
                {JSON.stringify(data, null, 2)}
              </pre>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};


export default AdminUserDetailPage;

/* ------------------------------------------------------------------ */
/* Bundle 127 - fine-grained RBAC toggles.                            */
/* Backend: /api/admin/rbac/permissions,                              */
/*          /api/admin/rbac/users/:id/permissions,                    */
/*          /api/admin/rbac/{grant,revoke} (super_admin gated).       */
/* Super-admins have all permissions implicitly; the card renders     */
/* toggles as always-on and disabled for them.                        */
/* ------------------------------------------------------------------ */
const RBAC_LABEL = {
  manage_introductions: 'Manage introductions',
  view_sensitive_connection_data:
    'View sensitive connection data (unmasked)',
  manage_success_stories: 'Manage success stories',
};
const RBAC_DESC = {
  manage_introductions:
    'Write access to /admin/introductions: mark identified, cancel, and add notes.',
  view_sensitive_connection_data:
    'Reveal full names, emails, and consent history on the connections admin views.',
  manage_success_stories:
    'Publish, unpublish, and edit testimonials on /admin/success-stories.',
};

const RbacPermissionsCard = ({ userId, role }) => {
  const [catalog, setCatalog] = useState([]);
  const [granted, setGranted] = useState([]);
  const [busy, setBusy] = useState({});
  const [loading, setLoading] = useState(true);
  const isSuper = role === 'super_admin';

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [c, g] = await Promise.all([
        api.get('/admin/rbac/permissions'),
        api.get(`/admin/rbac/users/${userId}/permissions`),
      ]);
      setCatalog(c.data.permissions || []);
      setGranted(g.data.permissions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Could not load permissions.');
    } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (perm, nextOn) => {
    setBusy((s) => ({ ...s, [perm]: true }));
    try {
      const path = nextOn ? '/admin/rbac/grant' : '/admin/rbac/revoke';
      await api.post(path, { user_id: userId, permission: perm });
      toast.success(`${RBAC_LABEL[perm] || perm} ${nextOn ? 'granted' : 'revoked'}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || `Failed to ${nextOn ? 'grant' : 'revoke'} ${perm}.`);
    } finally {
      setBusy((s) => ({ ...s, [perm]: false }));
    }
  };

  return (
    <Card data-testid="admin-user-detail-rbac-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary-700" />
          Fine-grained permissions
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-neutral-500">Loading permissions.</div>
        ) : (
          <div className="space-y-3">
            {isSuper && (
              <div className="text-[11px] text-neutral-500"
                    data-testid="rbac-super-admin-note">
                Super admins hold every permission implicitly. Toggles are
                shown for reference only.
              </div>
            )}
            {catalog.map((perm) => {
              const on = isSuper || granted.includes(perm);
              return (
                <label
                  key={perm}
                  className="flex items-start gap-3 p-2 rounded border border-neutral-100 hover:bg-neutral-50 cursor-pointer select-none"
                  data-testid={`rbac-row-${perm}`}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={isSuper || busy[perm]}
                    onChange={(e) => toggle(perm, e.target.checked)}
                    className="mt-1 accent-primary-800"
                    data-testid={`rbac-toggle-${perm}`}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-primary-900">
                      {RBAC_LABEL[perm] || perm}
                    </div>
                    <div className="text-[11px] text-neutral-500">
                      {RBAC_DESC[perm] || perm}
                    </div>
                  </div>
                </label>
              );
            })}
            {catalog.length === 0 && (
              <div className="text-sm text-neutral-500">No permissions defined.</div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/* Bundle 34 Task 3 - EditOnBehalfCard                                */
/* Admin (or super_admin) can silently edit whitelisted user fields.  */
/* Every save calls PATCH /admin/users/:id/edit-on-behalf and lands   */
/* in admin_audit_entries. The user gets NO email notification.       */
/* ------------------------------------------------------------------ */
const EDIT_ON_BEHALF_FIELDS = [
  { key: 'first_name', label: 'First name', kind: 'text',
    read: (u) => u.first_name },
  { key: 'last_name', label: 'Last name', kind: 'text',
    read: (u) => u.last_name },
  { key: 'preferred_name', label: 'Preferred name', kind: 'text',
    read: (u) => u.preferred_name },
  { key: 'phone', label: 'Phone', kind: 'text',
    read: (u) => u.phone },
  { key: 'about_me', label: 'Bio (about me)', kind: 'textarea',
    read: (u) => u?.profile?.about_me },
  { key: 'occupation', label: 'Occupation', kind: 'text',
    read: (u) => u?.profile?.occupation },
  { key: 'dob', label: 'Date of birth (YYYY-MM-DD)', kind: 'text',
    read: (u) => u?.profile?.dob },
  { key: 'gender', label: 'Gender', kind: 'text',
    read: (u) => u?.profile?.gender },
  { key: 'city', label: 'City', kind: 'text',
    read: (u) => u?.profile?.location?.city },
  { key: 'state', label: 'State', kind: 'text',
    read: (u) => u?.profile?.location?.state },
  { key: 'country', label: 'Country', kind: 'text',
    read: (u) => u?.profile?.location?.country },
  { key: 'instagram', label: 'Instagram', kind: 'text',
    read: (u) => u?.profile?.social_handles?.instagram },
  { key: 'twitter', label: 'Twitter', kind: 'text',
    read: (u) => u?.profile?.social_handles?.twitter },
  { key: 'x', label: 'X (formerly Twitter)', kind: 'text',
    read: (u) => u?.profile?.social_handles?.x },
  { key: 'linkedin', label: 'LinkedIn', kind: 'text',
    read: (u) => u?.profile?.social_handles?.linkedin },
  { key: 'tiktok', label: 'TikTok', kind: 'text',
    read: (u) => u?.profile?.social_handles?.tiktok },
  { key: 'facebook', label: 'Facebook', kind: 'text',
    read: (u) => u?.profile?.social_handles?.facebook },
  { key: 'threads', label: 'Threads', kind: 'text',
    read: (u) => u?.profile?.social_handles?.threads },
];

const EditOnBehalfCard = ({ user, onSaved }) => {
  const [editing, setEditing] = React.useState(null);
  const [draft, setDraft] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [reason, setReason] = React.useState('');

  const startEdit = (fieldKey) => {
    const spec = EDIT_ON_BEHALF_FIELDS.find((f) => f.key === fieldKey);
    setEditing(fieldKey);
    setReason('');
    setDraft(spec ? (spec.read(user) ?? '') : '');
  };
  const cancel = () => { setEditing(null); setDraft(''); setReason(''); };
  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const body = { fields: { [editing]: draft }, reason: reason.trim() };
      const { data } = await api.patch(
        `/admin/users/${user.id}/edit-on-behalf`,
        body,
      );
      if (data?.updated === false) {
        toast.info('No change detected.');
      } else {
        toast.success('Field saved. Recorded in the admin audit log.');
        if (onSaved) onSaved();
      }
      cancel();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (detail && (detail.message || detail))
        || 'Save failed. Check the console for details.';
      toast.error(typeof msg === 'string' ? msg : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="admin-user-detail-edit-on-behalf">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          Edit on behalf of user
          <span className="ml-2 text-[10px] text-neutral-400 font-normal">
            silent (no email); every save is hashed to admin audit
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {EDIT_ON_BEHALF_FIELDS.map((f) => {
            const value = f.read(user);
            const isEditing = editing === f.key;
            return (
              <div
                key={f.key}
                className="border border-neutral-200 rounded-md p-2 text-xs"
                data-testid={`admin-edit-behalf-field-${f.key}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    {f.label}
                  </div>
                  {!isEditing && (
                    <button
                      type="button"
                      onClick={() => startEdit(f.key)}
                      className="text-secondary-700 hover:text-secondary-900 hover:underline text-[11px]"
                      data-testid={`admin-edit-behalf-edit-${f.key}`}
                    >
                      Edit
                    </button>
                  )}
                </div>
                {!isEditing ? (
                  <div className="mt-1 text-primary-900 break-words min-h-[16px]">
                    {value != null && value !== '' ? String(value)
                      : <span className="text-neutral-400 italic">(empty)</span>}
                  </div>
                ) : (
                  <div className="mt-2 space-y-2">
                    {f.kind === 'textarea' ? (
                      <textarea
                        value={draft ?? ''}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={3}
                        className="w-full border border-neutral-300 rounded-md px-2 py-1 text-xs"
                        data-testid={`admin-edit-behalf-input-${f.key}`}
                      />
                    ) : (
                      <Input
                        value={draft ?? ''}
                        onChange={(e) => setDraft(e.target.value)}
                        data-testid={`admin-edit-behalf-input-${f.key}`}
                      />
                    )}
                    <Input
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Optional short reason for audit log"
                      maxLength={200}
                      data-testid={`admin-edit-behalf-reason-${f.key}`}
                    />
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        type="button" size="sm" variant="outline"
                        onClick={cancel} disabled={saving}
                        data-testid={`admin-edit-behalf-cancel-${f.key}`}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button" size="sm"
                        onClick={save} disabled={saving}
                        className="bg-primary-800 hover:bg-primary-900 text-white"
                        data-testid={`admin-edit-behalf-save-${f.key}`}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/*  Iteration 22 hotfix - full-visibility subcomponents so admin sees */
/*  EVERY field a user has ever filled in anywhere. Never hide.       */
/* ------------------------------------------------------------------ */

const ProgressionHeroAdmin = ({ shaped, recent }) => {
  if (!shaped) return null;
  const pct = shaped.level_progress_pct || 0;
  const rank = shaped.rank_display || `L${shaped.level}`;
  const nextRank = shaped.next_rank_display;
  const nextLevel = shaped.next_level;
  const intoLvl = shaped.points_into_level || 0;
  const needLvl = shaped.points_needed_this_level || 0;
  return (
    <Card data-testid="admin-user-detail-progression-hero">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
          <div className="text-sm font-semibold text-primary-900">
            {rank}
            {nextRank && nextLevel && (
              <span className="ml-2 text-[11px] text-neutral-500 font-normal">
                next rank: {nextRank} at L{nextLevel}
              </span>
            )}
          </div>
          <div className="text-[11px] font-mono text-neutral-600">
            {intoLvl}{needLvl > 0 ? ` / ${needLvl}` : ''} points this level
          </div>
        </div>
        <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-secondary-500"
            style={{ width: `${Math.max(2, Math.min(100, pct))}%` }}
            data-testid="admin-user-detail-progression-hero-bar"
          />
        </div>
        {recent && recent.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="admin-user-detail-progression-hero-recent">
            {recent.map((row) => (
              <span
                key={row.id}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                  row.points >= 0
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                    : 'bg-rose-50 text-rose-800 border-rose-200'
                }`}
                title={row.reason || row.description || row.kind}
              >
                {(row.kind || '').replace(/_/g, ' ')}
                <span className="font-mono">{row.points >= 0 ? '+' : ''}{row.points}</span>
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};


const RARITY_STYLE = {
  Common: 'bg-neutral-50 text-neutral-700 border-neutral-200',
  Uncommon: 'bg-blue-50 text-blue-800 border-blue-200',
  Rare: 'bg-secondary-50 text-secondary-800 border-secondary-200',
  Epic: 'bg-accent-50 text-accent-800 border-accent-300',
  Legendary: 'bg-amber-50 text-amber-900 border-amber-300',
};

// Bundle 23 hotfix - Bulletproof value formatter.
// Guarantees no raw JSON `{"city":"Dallas"...}` ever leaks to the UI.
// Every dict either routes through a known per-field formatter OR falls
// back to a small bullet list of key:value pairs. Empty values are
// skipped everywhere so we never render `LinkedIn: ""`.
const _isEmpty = (v) =>
  v == null || v === '' ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

const _formatLocation = (loc) => {
  const parts = ['city', 'state', 'country']
    .map((k) => (loc[k] || '').toString().trim())
    .filter(Boolean);
  return parts.length ? parts.join(', ') : null;
};

const _SOCIAL_LABEL = {
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  facebook: 'Facebook',
  twitter: 'Twitter',
  x: 'X',
};
const _SOCIAL_URL = {
  instagram: (h) => `https://instagram.com/${h.replace(/^@/, '')}`,
  linkedin: (h) => h.startsWith('http') ? h : `https://linkedin.com/in/${h}`,
  tiktok: (h) => `https://tiktok.com/@${h.replace(/^@/, '')}`,
  facebook: (h) => h.startsWith('http') ? h : `https://facebook.com/${h}`,
  twitter: (h) => `https://twitter.com/${h.replace(/^@/, '')}`,
  x: (h) => `https://x.com/${h.replace(/^@/, '')}`,
};

const _formatSocials = (socials) => {
  const rows = Object.entries(socials)
    .filter(([, v]) => !_isEmpty(v))
    .map(([k, v]) => {
      const label = _SOCIAL_LABEL[k] || k;
      const raw = String(v).trim();
      const display = (k === 'instagram' || k === 'tiktok' || k === 'twitter' || k === 'x')
        ? (raw.startsWith('@') ? raw : `@${raw}`)
        : raw;
      const href = _SOCIAL_URL[k] ? _SOCIAL_URL[k](raw) : null;
      return { key: k, label, display, href };
    });
  if (rows.length === 0) return null;
  return (
    <div className="space-y-0.5">
      {rows.map((r) => (
        <div key={r.key}>
          <span className="text-neutral-500">{r.label}: </span>
          {r.href ? (
            <a
              href={r.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondary-700 hover:text-secondary-900 underline"
              data-testid={`admin-user-detail-social-${r.key}`}
            >{r.display}</a>
          ) : (
            <span className="text-primary-900">{r.display}</span>
          )}
        </div>
      ))}
    </div>
  );
};

const _formatMarriagePrefs = (mp) => {
  const bits = [];
  const ageMin = mp.age_min ?? mp.ageMin;
  const ageMax = mp.age_max ?? mp.ageMax;
  if (!_isEmpty(ageMin) || !_isEmpty(ageMax)) {
    bits.push(`Age: ${ageMin || '?'} to ${ageMax || '?'}`);
  }
  ['religion', 'culture', 'lifestyle', 'notes', 'dealbreakers'].forEach((k) => {
    if (!_isEmpty(mp[k])) bits.push(`${k[0].toUpperCase()}${k.slice(1)}: ${mp[k]}`);
  });
  return bits.length ? bits.join(' | ') : null;
};

const _formatPhotos = (photos) => {
  // Just report a count - actual thumbs are rendered in the dedicated
  // PhotosGallery card. Never dump the URL blob here.
  if (Array.isArray(photos)) {
    return photos.length ? `${photos.length} pictures uploaded` : null;
  }
  if (photos && typeof photos === 'object') {
    const total = (photos.primary ? 1 : 0) + (photos.additional || []).length;
    return total ? `${total} pictures uploaded` : null;
  }
  return null;
};

const _formatVisibility = (vs) => {
  const on = Object.entries(vs).filter(([, v]) => v === true || v === 'public').map(([k]) => k);
  return on.length ? `Public: ${on.join(', ')}` : 'All private';
};

// Per-key dict formatters. Return a string OR a JSX node.
const _DICT_FORMATTERS = {
  location: _formatLocation,
  social_handles: _formatSocials,
  socials: _formatSocials,
  marriage_prefs: _formatMarriagePrefs,
  photos: _formatPhotos,
  visibility_settings: _formatVisibility,
};

const _genericDictBullets = (obj) => {
  const entries = Object.entries(obj).filter(([, v]) => !_isEmpty(v));
  if (entries.length === 0) return null;
  return (
    <div className="space-y-0.5">
      {entries.map(([k, v]) => (
        <div key={k}>
          <span className="text-neutral-500">{k.replace(/_/g, ' ')}: </span>
          <span className="text-primary-900">
            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
          </span>
        </div>
      ))}
    </div>
  );
};

const formatFieldValue = (v, fieldKey) => {
  if (_isEmpty(v)) return null;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) {
    if (v.every((x) => typeof x === 'string' || typeof x === 'number')) {
      return v.join(', ');
    }
    // Array of dicts - fall back to a bullet list of pretty strings.
    return (
      <div className="space-y-0.5">
        {v.map((item, i) => (
          <div key={i} className="text-primary-900">
            {typeof item === 'object' ? _genericDictBullets(item) : String(item)}
          </div>
        ))}
      </div>
    );
  }
  if (typeof v === 'object') {
    const formatter = fieldKey ? _DICT_FORMATTERS[fieldKey] : null;
    if (formatter) return formatter(v);
    return _genericDictBullets(v);
  }
  return String(v);
};

const ProfileSectionsGrid = ({ sections }) => {
  if (!sections || sections.length === 0) return null;
  return (
    <Card data-testid="admin-user-detail-profile-sections">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Profile sections (parity with user view)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {sections.map((s) => {
          const filled = s.filled_count || 0;
          const total = s.total_count || 0;
          const empty = filled === 0;
          const pct = s.completion_pct != null ? s.completion_pct : 0;
          const pctColor = pct >= 80 ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
            : pct >= 40 ? 'bg-amber-50 text-amber-800 border-amber-200'
            : 'bg-neutral-100 text-neutral-600 border-neutral-200';
          return (
            <div
              key={s.key}
              className="rounded-md border border-neutral-200 p-3"
              data-testid={`admin-user-detail-section-${s.key}`}
            >
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <div className="text-sm font-semibold text-primary-900">
                  Section {s.letter}: {s.display_name || s.key.replace(/_/g, ' ')}
                </div>
                <div className="flex items-center gap-1.5">
                  <span
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${pctColor}`}
                    data-testid={`admin-user-detail-section-${s.key}-pct`}
                  >
                    {pct}%
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                      empty
                        ? 'bg-neutral-100 text-neutral-500 border-neutral-200'
                        : 'bg-secondary-50 text-secondary-800 border-secondary-200'
                    }`}
                  >
                    {empty ? 'Not started' : `${filled}/${total}`}
                  </span>
                </div>
              </div>
              {empty ? (
                <div className="text-xs text-neutral-400 italic">
                  Not started by the user.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(s.fields).map(([k, v]) => {
                    const val = formatFieldValue(v, k);
                    if (val == null) return null;
                    return (
                      <div key={k} className="text-xs">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                          {k.replace(/_/g, ' ')}
                        </div>
                        <div className="text-primary-900 whitespace-pre-wrap break-words">
                          {val}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

const PhotoTile = ({ ph, index, displayName, targetEmail }) => {
  // Bundle 48.3 - if server already reported the blob is missing,
  // skip the Regenerate ritual and render the mailto card directly.
  const initialOrphan = typeof ph === 'object'
    && ph?.blob_status === 'orphan';
  const [state, setState] = React.useState({
    src: ph.thumb_url || ph.url || (typeof ph === 'string' ? ph : ''),
    href: ph.url || ph,
    needsRegen: !!ph.needs_regenerate,
    regenerating: false,
    error: initialOrphan,
    orphan: initialOrphan,
    orphanSteps: null,
  });
  const uploaded = ph.uploaded_at || ph.created_at;
  const onRegenerate = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!ph.id) {
      toast.error('Cannot regenerate: media id is missing.');
      return;
    }
    setState((s) => ({ ...s, regenerating: true, error: false, orphan: false }));
    try {
      const { data } = await api.post(`/admin/media/${ph.id}/regenerate-signed-url`);
      if (data && data.recovered === false) {
        setState((s) => ({
          ...s,
          regenerating: false,
          error: true,
          orphan: true,
          orphanSteps: data.steps || [],
          gone: data.reason === 'media_row_missing',
        }));
        const msg = data.reason === 'serve_failed'
          ? 'Photo file is unreachable. Ask the user to re-upload.'
          : data.reason === 'media_row_missing'
            ? 'This photo record is gone. The tile will disappear on next reload.'
            : 'This photo blob is missing. Ask the user to re-upload.';
        toast.error(msg);
        return;
      }
      setState({
        src: (data.thumb_url || data.url) + (data.url ? `#t=${Date.now()}` : ''),
        href: data.url,
        needsRegen: false,
        regenerating: false,
        error: false,
        orphan: false,
        orphanSteps: null,
        renderFailed: false,
        everRegenerated: true,
      });
      toast.success(data.method
        ? `Photo recovered via ${data.method}`
        : 'Signed URL regenerated');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Regenerate failed';
      toast.error(typeof msg === 'string' ? msg : 'Regenerate failed');
      setState((s) => ({ ...s, regenerating: false, error: true }));
    }
  };
  const onDiagnose = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!ph.id) return;
    try {
      const { data } = await api.get(`/admin/media/${ph.id}/diagnostic`);
      const w = window.open('', '_blank', 'noopener,noreferrer');
      if (w) {
        w.document.write(`<pre>${JSON.stringify(data, null, 2)}</pre>`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Diagnostic failed';
      toast.error(typeof msg === 'string' ? msg : 'Diagnostic failed');
    }
  };
  const showRegenBtn = (state.needsRegen || state.error) && !state.orphan;
  const mailSubject = encodeURIComponent('Please re-upload your profile picture');
  const mailBody = encodeURIComponent(
    `Hi ${displayName || 'there'},\n\nOne of your profile pictures on Dostination is no longer viewable due to a storage sync issue on our side. Could you please re-upload it from your profile page?\n\nThank you.`,
  );
  const mailtoHref = targetEmail
    ? `mailto:${targetEmail}?subject=${mailSubject}&body=${mailBody}`
    : null;
  return (
    <a
      href={state.href}
      target="_blank"
      rel="noopener noreferrer"
      className="block relative group"
      data-testid={`admin-user-detail-photo-${index}`}
    >
      <img
        src={state.src}
        alt={`${displayName} upload ${index + 1}`}
        className="w-40 h-40 rounded-lg object-cover bg-neutral-100 border border-neutral-200 group-hover:opacity-90 transition-opacity"
        loading="lazy"
        onError={() => setState((s) => {
          // Bundle 46 Task 1 - a render error after we already
          // regenerated at least once means the URL truly cannot be
          // served (blob missing or serve failing). Skip the loop and
          // switch to orphan / mailto UI directly.
          if (s.everRegenerated) {
            return { ...s, error: true, orphan: true };
          }
          return { ...s, error: true };
        })}
      />
      {uploaded && !showRegenBtn && !state.orphan && (
        <div className="absolute bottom-1 left-1 right-1 bg-black/60 text-white text-[10px] rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {fmtDate(uploaded)}
        </div>
      )}
      {showRegenBtn && (
        <button
          type="button"
          onClick={onRegenerate}
          disabled={state.regenerating}
          className="absolute inset-x-1 bottom-1 inline-flex items-center justify-center gap-1 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-semibold py-1 disabled:opacity-70"
          data-testid={`admin-user-detail-photo-regenerate-${index}`}
        >
          {state.regenerating ? (
            <><Loader2 className="h-3 w-3 animate-spin" /> Recovering...</>
          ) : (
            <><RefreshCw className="h-3 w-3" /> Regenerate</>
          )}
        </button>
      )}
      {state.orphan && (
        <div
          className="absolute inset-x-1 bottom-1 rounded-md bg-red-900/95 text-white text-[10px] p-1.5 space-y-1"
          data-testid={`admin-user-detail-photo-orphan-${index}`}
        >
          <div className="font-semibold">Photo blob missing.</div>
          <div className="flex items-center gap-2">
            {mailtoHref && (
              <a
                href={mailtoHref}
                className="underline hover:text-amber-200"
                onClick={(e) => e.stopPropagation()}
                data-testid={`admin-user-detail-photo-orphan-mailto-${index}`}
              >
                Ask user to re-upload
              </a>
            )}
            <button
              type="button"
              onClick={onDiagnose}
              className="underline hover:text-amber-200"
              data-testid={`admin-user-detail-photo-orphan-diagnose-${index}`}
            >
              Diagnose
            </button>
          </div>
        </div>
      )}
    </a>
  );
};

const PhotoTileKebabMenu = ({ ph, userId, onChanged }) => {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const fileRef = React.useRef(null);

  const onReplace = () => {
    setOpen(false);
    fileRef.current?.click();
  };
  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error('Please choose an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB.');
      return;
    }
    setBusy(true);
    try {
      // Delete old, then upload new. Two-step avoids server-side race.
      if (ph.id) {
        await api.delete(`/admin/media/${ph.id}/on-behalf`);
      }
      const form = new FormData();
      form.append('file', file);
      await api.post(`/admin/users/${userId}/upload-photo`, form);
      toast.success('Photo replaced. Recorded in the admin audit log.');
      if (onChanged) onChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (detail && (detail.message || detail)) || 'Replace failed.';
      toast.error(typeof msg === 'string' ? msg : 'Replace failed.');
    } finally {
      setBusy(false);
    }
  };
  const onDelete = async () => {
    setOpen(false);
    if (!ph.id) return;
    const ok = window.confirm(
      'Delete this photo on behalf of the user? This action is audit-logged.',
    );
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/admin/media/${ph.id}/on-behalf`);
      toast.success('Photo deleted. Recorded in the admin audit log.');
      if (onChanged) onChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (detail && (detail.message || detail)) || 'Delete failed.';
      toast.error(typeof msg === 'string' ? msg : 'Delete failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="absolute top-1 right-1 z-10" data-testid={`photo-tile-menu-${ph.id || 'unknown'}`}>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        disabled={busy}
        className="p-1 rounded-md bg-black/50 hover:bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Photo actions"
        data-testid={`photo-tile-menu-btn-${ph.id || 'unknown'}`}
      >
        <MoreVertical className="h-3.5 w-3.5" />
      </button>
      {open ? (
        <div
          className="absolute right-0 mt-1 min-w-[180px] rounded-md border border-neutral-200 bg-white shadow-lg py-1 text-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="block w-full text-left px-3 py-1.5 hover:bg-neutral-100"
            onClick={onReplace}
            data-testid={`photo-tile-replace-${ph.id || 'unknown'}`}
          >
            Replace
          </button>
          <button
            type="button"
            className="block w-full text-left px-3 py-1.5 text-red-700 hover:bg-red-50"
            onClick={onDelete}
            data-testid={`photo-tile-delete-${ph.id || 'unknown'}`}
          >
            Delete
          </button>
        </div>
      ) : null}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={onFilePicked}
        className="hidden"
        data-testid={`photo-tile-file-input-${ph.id || 'unknown'}`}
      />
    </div>
  );
};

const PhotoUploadTile = ({ userId, onChanged }) => {
  const [busy, setBusy] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const fileRef = React.useRef(null);

  const uploadFile = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error('Please choose an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB.');
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post(`/admin/users/${userId}/upload-photo`, form);
      toast.success('Photo uploaded. Recorded in the admin audit log.');
      if (onChanged) onChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (detail && (detail.message || detail)) || 'Upload failed.';
      toast.error(typeof msg === 'string' ? msg : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  return (
    <button
      type="button"
      onClick={() => fileRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      disabled={busy}
      className={`w-40 h-40 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 text-xs text-neutral-500 hover:text-neutral-800 hover:border-neutral-400 transition-colors ${
        dragOver ? 'border-primary-600 bg-primary-50 text-primary-900' : 'border-neutral-300 bg-neutral-50'
      } ${busy ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
      data-testid="admin-photo-upload-tile"
    >
      <UploadCloud className="h-6 w-6" />
      <span className="font-medium text-center leading-tight px-2">
        {busy ? 'Uploading...' : 'Upload on user\u2019s behalf'}
      </span>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) uploadFile(f);
        }}
        className="hidden"
        data-testid="admin-photo-upload-file-input"
      />
    </button>
  );
};

const _sourceBadgeMeta = {
  profile: { label: 'Profile edit',
              cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  rsvp: { label: 'From RSVP',
           cls: 'bg-secondary-50 text-secondary-800 border-secondary-200' },
  media: { label: 'Direct upload',
            cls: 'bg-neutral-100 text-neutral-700 border-neutral-200' },
};

const PhotoSourceBadge = ({ source }) => {
  const meta = _sourceBadgeMeta[source] || _sourceBadgeMeta.media;
  return (
    <span
      className={`absolute bottom-1 left-1 z-[1] rounded px-1.5 py-0.5 text-[9px] font-semibold border ${meta.cls}`}
      data-testid={`photo-tile-source-${source || 'unknown'}`}
    >
      {meta.label}
    </span>
  );
};

const PhotosGallery = ({ photos, displayName, targetEmail, userId, onChanged }) => (
  <Card data-testid="admin-user-detail-photos">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm inline-flex items-center gap-2">
        <ImageIcon className="h-3.5 w-3.5" /> Uploaded pictures
        <span
          className="text-[10px] text-neutral-400 font-normal"
          data-testid="admin-user-detail-photos-count"
        >
          ({photos.length})
        </span>
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="flex flex-wrap gap-3">
        {photos.map((ph, i) => (
          <div key={(typeof ph === 'object' && ph.id) || i} className="relative group">
            <PhotoTile
              ph={ph}
              index={i}
              displayName={displayName}
              targetEmail={targetEmail}
            />
            {typeof ph === 'object' && ph.source ? (
              <PhotoSourceBadge source={ph.source} />
            ) : null}
            {userId && typeof ph === 'object' && ph.id ? (
              <PhotoTileKebabMenu ph={ph} userId={userId} onChanged={onChanged} />
            ) : null}
          </div>
        ))}
        {userId ? <PhotoUploadTile userId={userId} onChanged={onChanged} /> : null}
      </div>
      <p className="mt-3 text-[11px] text-neutral-500">
        Uploads and edits on behalf of users are logged. The user&apos;s own
        dashboard will show them in their Recent activity feed.
      </p>
    </CardContent>
  </Card>
);

const AchievementsBlock = ({ unlocks }) => (
  <Card data-testid="admin-user-detail-achievements-list">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm inline-flex items-center gap-2">
        <Award className="h-3.5 w-3.5" /> Achievements
        <span className="text-[10px] text-neutral-400 font-normal">
          ({unlocks.length})
        </span>
      </CardTitle>
    </CardHeader>
    <CardContent>
      {unlocks.length === 0 ? (
        <div className="text-sm text-neutral-500 italic">
          No achievements unlocked yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {unlocks.map((a) => (
            <li
              key={a.code + (a.unlocked_at || '')}
              className="flex items-start gap-3 p-2 rounded border border-neutral-100 hover:bg-neutral-50"
              data-testid={`admin-user-detail-achievement-${a.code}`}
            >
              {a.icon_url ? (
                <img
                  src={a.icon_url}
                  alt={a.title || a.code}
                  className="w-10 h-10 rounded-md object-cover bg-neutral-100 shrink-0"
                  loading="lazy"
                />
              ) : (
                <div className="w-10 h-10 rounded-md bg-secondary-50 border border-secondary-200 flex items-center justify-center shrink-0">
                  <Trophy className="h-4 w-4 text-secondary-700" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-primary-900 truncate">
                  {a.title || a.code}
                </div>
                {a.description && (
                  <div className="text-xs text-neutral-600 line-clamp-2">
                    {a.description}
                  </div>
                )}
                <div className="text-[11px] text-neutral-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>{fmtDate(a.unlocked_at)}</span>
                  {a.rarity && (
                    <Badge
                      variant="outline"
                      className={`h-4 text-[9px] px-1.5 ${RARITY_STYLE[a.rarity] || RARITY_STYLE.Common}`}
                    >
                      {a.rarity}
                    </Badge>
                  )}
                  {a.bonus_points != null && (
                    <span className="font-mono text-emerald-700">
                      +{a.bonus_points}
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </CardContent>
  </Card>
);

const PointEventsBlock = ({ events }) => {
  const [expanded, setExpanded] = useState(false);
  const initial = 10;
  const show = expanded ? events : events.slice(0, initial);
  return (
    <Card data-testid="admin-user-detail-point-events">
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-sm inline-flex items-center gap-2">
          <Clock className="h-3.5 w-3.5" /> Point events
          <span className="text-[10px] text-neutral-400 font-normal">
            ({events.length})
          </span>
        </CardTitle>
        {events.length > initial && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-secondary-700 hover:text-secondary-900"
            data-testid="admin-user-detail-point-events-toggle"
          >
            {expanded ? 'Show recent only' : `Show all (${events.length})`}
          </button>
        )}
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <div className="text-sm text-neutral-500 italic">No point events yet.</div>
        ) : (
          <ul className="space-y-1.5">
            {show.map((row) => (
              <li
                key={row.id}
                className="flex items-center justify-between gap-2 text-sm p-2 rounded border border-neutral-100"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-primary-900 font-medium truncate">
                    {row.kind}
                  </div>
                  <div className="text-[11px] text-neutral-500 truncate">
                    {row.reason || row.description || row.idempotency_key || fmtDate(row.created_at)}
                    {row.source_entity_type && ` · ${row.source_entity_type}`}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-mono text-sm ${row.points >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {row.points >= 0 ? '+' : ''}{row.points}
                  </div>
                  <div className="text-[10px] text-neutral-400">
                    {fmtDate(row.created_at)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

const ReferralDownstreamBlock = ({ code, downstream }) => {
  if (!code) return null;
  return (
    <Card data-testid="admin-user-detail-referral-downstream-list">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm inline-flex items-center gap-2">
          <Users className="h-3.5 w-3.5" /> Referred users
          <span className="text-[10px] text-neutral-400 font-normal">
            ({downstream.length})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {downstream.length === 0 ? (
          <div className="text-sm text-neutral-500 italic">
            No downstream users yet.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {downstream.map((du) => (
              <li
                key={du.id}
                className="flex items-center justify-between gap-2 text-sm p-2 rounded border border-neutral-100 hover:bg-neutral-50"
              >
                <Link
                  to={`/admin/users/${du.id}`}
                  className="min-w-0 flex-1 text-primary-900 hover:underline truncate"
                >
                  {[du.first_name, du.last_name].filter(Boolean).join(' ') || du.email}
                  <span className="text-neutral-500 text-xs ml-2">{du.email}</span>
                </Link>
                <span className="text-[10px] text-neutral-400 shrink-0">
                  {fmtDate(du.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

const RawUserBlock = ({ user }) => {
  const [open, setOpen] = useState(false);
  const pretty = JSON.stringify(user, null, 2);
  return (
    <Card data-testid="admin-user-detail-raw-user">
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-600 hover:text-primary-900"
            data-testid="admin-user-detail-raw-toggle"
            aria-expanded={open}
          >
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <FileJson className="h-3.5 w-3.5" />
            Raw user document (debug)
          </button>
          {open && (
            <CopyJsonButton
              value={pretty}
              testId="admin-user-detail-raw-copy"
            />
          )}
        </div>
        {open && (
          <pre
            className="mt-2 text-[11px] leading-relaxed bg-neutral-50 border border-neutral-200 rounded p-3 overflow-x-auto max-h-[500px] overflow-y-auto whitespace-pre-wrap break-words"
            data-testid="admin-user-detail-raw-body"
          >
            {pretty}
          </pre>
        )}
      </CardContent>
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/* Bundle 36 Task A - Admin actions preview panel                     */
/* Renders the last 5 admin_audit_entries scoped to this target user. */
/* Field NAMES only, no plaintext PII, no hash blobs.                 */
/* ------------------------------------------------------------------ */
const AdminActionsPreviewCard = ({ userId }) => {
  const [entries, setEntries] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(
          `/admin/users/${userId}/admin-actions-preview?limit=5`,
        );
        if (mounted) setEntries(data.entries || []);
      } catch (_) {
        // silent - card is optional
      } finally {
        if (mounted) setLoaded(true);
      }
    })();
    return () => { mounted = false; };
  }, [userId]);
  const relTime = (iso) => {
    if (!iso) return '';
    try {
      const then = new Date(iso).getTime();
      const now = Date.now();
      const s = Math.max(1, Math.round((now - then) / 1000));
      if (s < 60) return `${s}s ago`;
      const m = Math.round(s / 60);
      if (m < 60) return `${m}m ago`;
      const h = Math.round(m / 60);
      if (h < 48) return `${h}h ago`;
      const d = Math.round(h / 24);
      return `${d}d ago`;
    } catch (_) { return ''; }
  };
  return (
    <Card data-testid="admin-user-detail-actions-preview">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between gap-2">
          <span>Recent admin actions on this user</span>
          <Link
            to={`/admin/audit?target_id=${userId}`}
            className="text-[11px] text-secondary-700 hover:underline"
            data-testid="admin-user-detail-actions-preview-full-audit"
          >
            View full audit
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!loaded ? (
          <div className="text-xs text-neutral-400">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="text-xs text-neutral-500 italic">
            No admin actions on this account yet.
          </div>
        ) : (
          <div className="rounded-md border border-neutral-200 overflow-hidden text-xs"
                data-testid="admin-user-detail-actions-preview-table">
            <table className="w-full">
              <thead className="bg-neutral-50 text-neutral-600 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-2 py-1.5">Time</th>
                  <th className="text-left px-2 py-1.5">Actor</th>
                  <th className="text-left px-2 py-1.5">Action</th>
                  <th className="text-left px-2 py-1.5">Fields</th>
                  <th className="text-left px-2 py-1.5">Reason</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i} className="border-t border-neutral-100"
                      data-testid={`admin-user-detail-actions-preview-row-${i}`}>
                    <td className="px-2 py-1.5 text-neutral-600">{relTime(e.timestamp)}</td>
                    <td className="px-2 py-1.5 font-mono text-neutral-700">{e.actor_email || e.actor_id || '-'}</td>
                    <td className="px-2 py-1.5">{e.action_label || e.action}</td>
                    <td className="px-2 py-1.5 text-neutral-600">
                      {(e.fields || []).join(', ') || '-'}
                    </td>
                    <td className="px-2 py-1.5 text-neutral-500">{e.reason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
